import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Icon, PasswordField, TextField } from '@/design-system'
import { isAppError } from '@/services/supabase/client'
import {
  checkUsername,
  signUp,
  validateEmail,
  validatePassword,
  validateUsername,
} from './api'
import { AppMark } from './AppMark'
import './auth.css'

type Availability = 'idle' | 'checking' | 'free' | 'taken' | 'invalid'

export function RegisterScreen() {
  const navigate = useNavigate()
  const [displayName, setDisplayName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [availability, setAvailability] = useState<Availability>('idle')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Check the handle while it is typed, so nobody fills in a whole form only to
  // be told the name was taken.
  useEffect(() => {
    const value = username.trim().toLowerCase()
    if (!value) return setAvailability('idle')
    if (validateUsername(value)) return setAvailability('invalid')

    setAvailability('checking')
    const timer = setTimeout(async () => {
      const free = await checkUsername(value)
      setAvailability(free ? 'free' : 'taken')
    }, 420)
    return () => clearTimeout(timer)
  }, [username])

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (!displayName.trim()) return setError('Escribe cómo quieres que te llamemos.')
    const usernameError = validateUsername(username)
    if (usernameError) return setError(usernameError)
    if (availability === 'taken') return setError('Ese nombre de usuario ya está cogido.')
    const emailError = validateEmail(email)
    if (emailError) return setError(emailError)
    const passwordError = validatePassword(password)
    if (passwordError) return setError(passwordError)
    if (password2 !== password) return setError('Las contraseñas no coinciden.')

    setBusy(true)
    try {
      const { needsConfirmation } = await signUp({ email, password, username, displayName })
      if (needsConfirmation) {
        navigate('/confirma-correo', {
          replace: true,
          state: { email: email.trim(), justSent: true },
        })
      } else {
        navigate('/bienvenida', { replace: true })
      }
    } catch (e) {
      setError(isAppError(e) ? e.message : 'No se ha podido crear la cuenta.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <header className="auth__head">
        <AppMark size={84} />
        <h1 className="auth__mark">
          Cat<span>GO</span>
        </h1>
        <p className="auth__tagline">Recuerda cada gato que conozcas</p>
      </header>

      <form className="auth__form glass" onSubmit={onSubmit} noValidate>
        <h2 className="auth__title">Crear cuenta</h2>

        <TextField
          label="Tu nombre"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          autoComplete="name"
          maxLength={40}
          placeholder="Helena"
        />

        <TextField
          label="Nombre de usuario"
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
          autoCapitalize="none"
          autoComplete="username"
          maxLength={20}
          placeholder="helena"
          hint="Así te encontrarán otras personas: @helena"
        />

        {availability !== 'idle' && (
          <span className="auth__availability" data-state={availability}>
            {availability === 'checking' && 'Comprobando…'}
            {availability === 'free' && (
              <>
                <Icon name="check" size={14} strokeWidth={2.2} />@{username} está libre
              </>
            )}
            {availability === 'taken' && (
              <>
                <Icon name="close" size={14} strokeWidth={2.2} />@{username} ya está cogido
              </>
            )}
            {availability === 'invalid' && (validateUsername(username) ?? '')}
          </span>
        )}

        <TextField
          label="Correo"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@correo.com"
        />

        <PasswordField
          label="Contraseña"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="Al menos 8 caracteres, con letras y números."
        />

        <PasswordField
          label="Repite la contraseña"
          autoComplete="new-password"
          value={password2}
          onChange={(e) => setPassword2(e.target.value)}
          error={error}
        />

        <div className="auth__actions">
          <Button type="submit" size="lg" block loading={busy}>
            Crear cuenta
          </Button>
          <p className="auth__body" style={{ textAlign: 'center' }}>
            Tu colección empieza cerrada. Solo entra quien tú aceptes.
          </p>
          {/* En otra pestaña, para que el formulario a medias no se pierda. (§H261) */}
          <p className="auth__legal">
            Al crear la cuenta aceptas las{' '}
            <a href="/condiciones" target="_blank" rel="noopener">
              condiciones de uso
            </a>{' '}
            y confirmas que has leído el{' '}
            <a href="/privacidad" target="_blank" rel="noopener">
              aviso de privacidad
            </a>
            .
          </p>
        </div>
      </form>

      <footer className="auth__foot">
        <p className="auth__link">
          ¿Ya tienes cuenta? <Link to="/entrar">Entrar</Link>
        </p>
      </footer>
    </div>
  )
}
